import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CHROME_EXTENSION_URL } from '../lib/constants';
import { capture } from '../lib/analytics';
import { ChromeIcon } from './shared/ChromeIcon';
import { useInstallReminderDismissal } from '../hooks/useInstallReminderDismissal';

/** Compact reminder outside the dashboard. Shares its dismissal preference
 * with the dashboard card so changing pages does not repeat an offer the
 * user already dismissed. Pending onboarding keeps its dedicated CTA.
 */
export function ExtensionInstallBanner({ detected, checking }: { detected: boolean; checking: boolean }) {

  const { pathname } = useLocation();
  const { dismissed, dismiss } = useInstallReminderDismissal();
  // Dashboard has its own compact install reminder, so skip the compact banner there
  const onDashboard = pathname === '/dashboard';
  const visible = !checking && !detected && !onDashboard && !dismissed;
  const firedRef = useRef(false);
  useEffect(() => {
    if (visible && !firedRef.current) {
      firedRef.current = true;
      capture('extension_install_banner_shown', { source: 'global_banner' });
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <aside
      aria-label="Install extension"
      className="relative bg-primary-500 text-white flex flex-wrap items-center justify-center gap-x-3 gap-y-2 pl-4 pr-12 py-3 text-sm"
    >
      <span className="inline-flex items-center gap-2">
        <ChromeIcon className="w-4 h-4 flex-shrink-0" />
        <span className="[text-wrap:balance]">
          Capture bugs on any public page from this dashboard. Install the Chrome extension for localhost, staging and pages behind a login.
        </span>
      </span>
      <a
        href={CHROME_EXTENSION_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0 font-semibold text-white underline decoration-white/60 underline-offset-2 transition-colors hover:decoration-white"
        onClick={() => {
          capture('dashboard_extension_install_click', { source: 'global_banner' });
        }}
      >
        <span className="text-cap-trim block">Install extension &rarr;</span>
      </a>
      <button type="button" className="absolute top-2 right-2 p-1 rounded hover:bg-white/10" aria-label="Dismiss install reminder" onClick={dismiss}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </aside>
  );
}
